<script>
import axios from "axios";
import { toast, variants } from "@/helpers/toaster";

const blankCosignee = () => ({
  name: "",
  street_and_number: "",
  zipcode: "",
  city: "",
  country_code: "RS",
  contact_name: "",
  phone: "",
  email: "",
});

const blankFields = () => ({
  sender_id: null,
  reference: "",
  payment_by: "sender",
  number_of_packages: 1,
  is_pallet: false,
  value: null,
  cod_amount: null,
  note: "",
});

const blankDirty = () => ({
  reference: false,
  number_of_packages: false,
  note: false,
});

export default {
  name: "ShipmentBatchPreview",
  props: {
    otpremnice: { type: Array, required: true },
  },
  data() {
    return {
      loading: true,
      saving: false,
      regrouping: false,
      senders: [],
      groups: [], // [{ ids, cosignee, form, objekatResolved, addressSource, expanded, selectedIds, result }]
      drafts: {},
      // Toolbar shared across pending groups. The sender can be applied to
      // every pending group with one click — saves the operator from
      // re-entering the sender per card when it's the same for the whole
      // batch (the common case).
      bulkDefaults: {
        sender_id: null,
      },
    };
  },
  computed: {
    rowsById() {
      const m = {};
      for (const o of this.otpremnice) m[o.id] = o;
      return m;
    },
    pendingGroups() {
      return this.groups.filter((g) => !g.result || !g.result.ok);
    },
    successCount() {
      return this.groups.filter((g) => g.result && g.result.ok).length;
    },
    failureCount() {
      return this.groups.filter((g) => g.result && !g.result.ok).length;
    },
    readyCount() {
      return this.pendingGroups.filter((g) => this.isGroupValid(g)).length;
    },
    invalidCount() {
      return this.pendingGroups.length - this.readyCount;
    },
    totalPackagesAll() {
      return this.pendingGroups.reduce(
        (sum, g) => sum + (Number(g.form.number_of_packages) || 0),
        0,
      );
    },
    totalValueAll() {
      return this.pendingGroups.reduce(
        (sum, g) => sum + (Number(g.form.value) || 0),
        0,
      );
    },
    canSubmit() {
      if (this.saving) return false;
      if (!this.pendingGroups.length) return false;
      return this.pendingGroups.every((g) => this.isGroupValid(g));
    },
    invalidGroupsHint() {
      const reasons = [];
      for (const [i, g] of this.groups.entries()) {
        const r = this.groupInvalidReason(g);
        if (r) reasons.push(`Grupa #${i + 1}: ${r}`);
      }
      return reasons;
    },
    /**
     * Sender id shown in the global picker: returns the common value if
     * every pending group has the same sender, otherwise null so the
     * dropdown reads as "no choice yet".
     */
    globalSenderId() {
      if (!this.pendingGroups.length) return null;
      const first = this.pendingGroups[0].form.sender_id ?? null;
      for (const g of this.pendingGroups) {
        if ((g.form.sender_id ?? null) !== first) return null;
      }
      return first;
    },
  },
  async mounted() {
    await this.loadSenders();
    await this.buildInitialGroups();
    this.seedBulkDefaultsFromGroups();
    this.loading = false;
  },
  methods: {
    blankGroup(ids = []) {
      return {
        ids: [...ids],
        cosignee: blankCosignee(),
        form: blankFields(),
        objekatResolved: false,
        addressSource: null,
        expanded: false,
        selectedIds: [],
        dirty: blankDirty(),
        result: null,
      };
    },
    /**
     * Collapse every group. Used after initial preview load and after bulk
     * regroup operations (auto-grupisi / razgrupisi) so the operator can't
     * accidentally edit fields and must explicitly open a card.
     */
    autoCollapseReady() {
      for (const g of this.groups) g.expanded = false;
    },
    seedBulkDefaultsFromGroups() {
      const first = this.groups.find((g) => !g.result || !g.result.ok);
      if (!first) return;
      this.bulkDefaults = {
        sender_id: first.form.sender_id ?? null,
      };
    },
    async loadSenders() {
      const { data } = await axios.get("/super-admin/city-express/senders");
      this.senders = data.data || [];
    },
    /**
     * Auto-group selected otpremnice by (kupacId, objekatId). All otpremnice
     * for the same kupac+objekat land in a single group.
     * For each group, fetch a draft preview and seed cosignee/form.
     */
    async buildInitialGroups() {
      const buckets = new Map();
      for (const o of this.otpremnice) {
        const key = `${o.kupacId}::${o.objekatId}`;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(o);
      }

      const groups = [];
      for (const arr of buckets.values()) {
        groups.push(this.blankGroup(arr.map((x) => x.id)));
      }

      this.groups = groups;
      await this.refreshGroupDrafts(this.groups);
      this.autoCollapseReady();
    },
    /**
     * Fetch drafts for one or more groups in a single backend round-trip.
     * Replaces the old per-group fan-out which fired N parallel /preview
     * requests (each one re-walking the Wings otpremnice list).
     *
     * Already-sent groups (`result.ok === true`) and empty groups are
     * filtered out client-side; the latter are reset locally.
     */
    async refreshGroupDrafts(groups) {
      const targets = [];
      for (const g of groups) {
        if (g.result && g.result.ok) continue;
        if (!g.ids.length) {
          g.cosignee = blankCosignee();
          g.form = blankFields();
          g.dirty = blankDirty();
          g.objekatResolved = false;
          continue;
        }
        targets.push(g);
      }
      if (!targets.length) return;

      let resp;
      try {
        resp = await axios.post(
          "/super-admin/city-express/shipments/preview-batch",
          {
            groups: targets.map((g) => ({ wings_otpremnica_ids: g.ids })),
          },
        );
      } catch (err) {
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Greška u pripremi grupa.";
        toast(msg, variants.DANGER);
        return;
      }

      const results = (resp.data && resp.data.data) || [];
      results.forEach((r, i) => {
        const group = targets[i];
        if (!group) return;
        if (!r || !r.ok) {
          toast(
            `Grupa: ${(r && r.error) || "nepoznata greška"}`,
            variants.DANGER,
          );
          return;
        }
        this.applyDraftToGroup(group, r.draft || {});
      });
    },
    /**
     * Merge a server-side draft into a group's local state without clobbering
     * any edits the user has already made.
     */
    applyDraftToGroup(group, d) {
      group.objekatResolved = !!d.objekat_resolved;
      group.addressSource = d.address_source ?? null;
      const seedCosignee = { ...blankCosignee(), ...(d.cosignee || {}) };
      for (const k of Object.keys(seedCosignee)) {
        if (!group.cosignee[k]) group.cosignee[k] = seedCosignee[k];
      }
      if (group.form.sender_id == null)
        group.form.sender_id = d.sender_id ?? null;
      if (!group.dirty.reference) group.form.reference = d.reference || "";
      if (d.payment_by) group.form.payment_by = d.payment_by;
      if (!group.dirty.number_of_packages && d.number_of_packages)
        group.form.number_of_packages = Number(d.number_of_packages);
      group.form.value = d.value != null ? Number(d.value) : 0;
      if (!group.dirty.note) group.form.note = d.note || "";
    },
    markDirty(group, field) {
      group.dirty[field] = true;
    },
    singleKupacInGroup(group) {
      if (group.ids.length === 0) return false;
      const first = this.rowsById[group.ids[0]];
      if (!first) return true;
      return group.ids.every((id) => {
        const r = this.rowsById[id];
        return r && r.kupacId === first.kupacId;
      });
    },
    isGroupValid(group) {
      if (!group.ids.length) return false;
      if (!this.singleKupacInGroup(group)) return false;
      const c = group.cosignee;
      return !!(
        c.name &&
        c.street_and_number &&
        c.zipcode &&
        c.city &&
        c.country_code
      );
    },
    /**
     * Short reason why a pending group can't be submitted, or null if it
     * looks fine. Returned string is meant to be shown right in the
     * collapsed card header so the operator doesn't have to open the card
     * to find out what's wrong.
     */
    groupInvalidReason(group) {
      if (group.result && group.result.ok) return null;
      if (!group.ids.length) return "prazna grupa";
      if (!this.singleKupacInGroup(group)) return "različiti kupci u grupi";
      const c = group.cosignee;
      if (!c.name) return "nedostaje naziv primaoca";
      if (!c.street_and_number) return "nedostaje adresa primaoca";
      if (!c.zipcode) return "nedostaje poštanski broj";
      if (!c.city) return "nedostaje grad";
      if (!c.country_code) return "nedostaje država";
      return null;
    },
    addEmptyGroup() {
      this.groups.push(this.blankGroup([]));
    },
    removeGroup(idx) {
      const g = this.groups[idx];
      if (g && g.result && g.result.ok) return;
      this.groups.splice(idx, 1);
    },
    async removeFromGroup(group, id) {
      group.ids = group.ids.filter((x) => x !== id);
      group.selectedIds = group.selectedIds.filter((x) => x !== id);
      await this.refreshGroupDrafts([group]);
    },
    async moveBetweenGroups(fromIdx, toIdx, id) {
      const from = this.groups[fromIdx];
      const to = this.groups[toIdx];
      if (!from || !to || from === to) return;
      if (to.result && to.result.ok) return;
      from.ids = from.ids.filter((x) => x !== id);
      from.selectedIds = from.selectedIds.filter((x) => x !== id);
      to.ids.push(id);
      await this.refreshGroupDrafts([from, to]);
    },
    /**
     * Pull a single otpremnica out of its current group into a brand-new
     * shipment of its own (Q1). No-op for already-created groups or when the
     * row is already alone in its group.
     */
    async extractToOwnGroup(fromGroup, id) {
      if (this.regrouping) return;
      if (fromGroup.result && fromGroup.result.ok) return;
      if (fromGroup.ids.length <= 1) return;
      fromGroup.ids = fromGroup.ids.filter((x) => x !== id);
      fromGroup.selectedIds = fromGroup.selectedIds.filter((x) => x !== id);
      const newGroup = this.blankGroup([id]);
      this.groups.push(newGroup);
      await this.refreshGroupDrafts([fromGroup, newGroup]);
    },
    async splitToOnePer() {
      if (this.regrouping) return;
      this.regrouping = true;
      try {
        for (const g of this.groups) g.expanded = false;
        const flat = [];
        const keep = [];
        for (const g of this.groups) {
          if (g.result && g.result.ok) {
            keep.push(g);
            continue;
          }
          for (const id of g.ids) flat.push(id);
        }
        const newGroups = flat.map((id) => this.blankGroup([id]));
        this.groups = [...keep, ...newGroups];
        await this.refreshGroupDrafts(newGroups);
        this.autoCollapseReady();
      } finally {
        this.regrouping = false;
      }
    },
    async regroupByKupac() {
      if (this.regrouping) return;
      this.regrouping = true;
      try {
        for (const g of this.groups) g.expanded = false;
        const flat = [];
        const keep = [];
        for (const g of this.groups) {
          if (g.result && g.result.ok) {
            keep.push(g);
            continue;
          }
          for (const id of g.ids) flat.push(id);
        }
        const buckets = new Map();
        for (const id of flat) {
          const o = this.rowsById[id];
          if (!o) continue;
          const key = `${o.kupacId}::${o.objekatId}`;
          if (!buckets.has(key)) buckets.set(key, []);
          buckets.get(key).push(id);
        }
        const newGroups = [];
        for (const arr of buckets.values()) {
          newGroups.push(this.blankGroup(arr));
        }
        this.groups = [...keep, ...newGroups];
        await this.refreshGroupDrafts(newGroups);
        this.autoCollapseReady();
      } finally {
        this.regrouping = false;
      }
    },
    /**
     * Push the bulk-defaults toolbar values into every pending group's form.
     * Only fields the user has actually set in bulkDefaults overwrite per-group
     * values — empty bulk fields are no-ops so the operator can apply just one
     * setting (e.g. only the sender) without wiping anything else.
     */
    applyBulkDefaults() {
      let touched = 0;
      for (const g of this.pendingGroups) {
        if (this.bulkDefaults.sender_id != null) {
          g.form.sender_id = this.bulkDefaults.sender_id;
        }
        touched++;
      }
      if (touched > 0) {
        toast(`Primenjeno na ${touched} grupa.`, variants.SUCCESS);
      }
    },
    /**
     * Set sender_id on every pending group at once. Called by the global
     * sender picker rendered above the group list.
     */
    applyGlobalSender(senderId) {
      const id = senderId === "" ? null : senderId;
      for (const g of this.pendingGroups) {
        g.form.sender_id = id;
      }
    },
    toggleExpand(group) {
      group.expanded = !group.expanded;
    },
    expandAll() {
      for (const g of this.groups) g.expanded = true;
    },
    collapseAll() {
      for (const g of this.groups) g.expanded = false;
    },
    rowFor(id) {
      return this.rowsById[id] || {};
    },
    cosigneeIsComplete(c) {
      return !!(
        c &&
        c.name &&
        c.street_and_number &&
        c.zipcode &&
        c.city &&
        c.country_code
      );
    },
    /**
     * Address chip for the card header. Three states:
     *   - ok      : address came back from Wings (objekat or kupac)
     *   - warn    : came from kupac as fallback (objekat missing)
     *   - missing : nothing resolved; operator must enter manually
     */
    addressChip(group) {
      if (!group.ids.length) {
        return { tone: "neutral", variant: "secondary", text: "Prazna grupa" };
      }
      if (group.objekatResolved && group.addressSource === "objekat") {
        return {
          tone: "ok",
          variant: "success",
          text: "Adresa: objekat",
        };
      }
      if (group.objekatResolved && group.addressSource === "kupac") {
        // Pulling the address from the customer (kupac) is a perfectly valid
        // fallback, so present it as a neutral, informational note rather than
        // a yellow warning that suggests something is wrong. Use the darker
        // "secondary" tone so the chip is clearly legible (a "light" badge
        // washed out against the card header).
        return {
          tone: "neutral",
          variant: "secondary",
          text: "Adresa: kupac",
          tip: "Adresa preuzeta sa kupca",
        };
      }
      if (this.cosigneeIsComplete(group.cosignee)) {
        return {
          tone: "ok",
          variant: "info",
          text: "Adresa: ručno uneta",
        };
      }
      return {
        tone: "missing",
        variant: "danger",
        text: "Adresa: nedostaje",
      };
    },
    headerTitle(group) {
      if (!group.ids.length) return "(prazna grupa)";
      const r = this.rowsById[group.ids[0]];
      if (!r) return "—";
      return r.objekat ? `${r.kupac} — ${r.objekat}` : r.kupac;
    },
    groupTotalPackages(group) {
      // Reflect what will actually go on the City Express label, which is the
      // value of the form field (operator can override). Fall back to the sum
      // parsed from interna_napomena when the form hasn't been seeded yet.
      if (group.form && group.form.number_of_packages > 0) {
        return Number(group.form.number_of_packages);
      }
      return group.ids.reduce(
        (s, id) => s + (Number(this.rowsById[id]?.brojPaketa) || 0),
        0,
      );
    },
    groupTotalValue(group) {
      if (group.form && group.form.value != null && group.form.value !== "") {
        return Number(group.form.value);
      }
      return group.ids.reduce(
        (s, id) => s + (Number(this.rowsById[id]?.vrednost) || 0),
        0,
      );
    },
    pickToggleId(group, id) {
      const i = group.selectedIds.indexOf(id);
      if (i >= 0) group.selectedIds.splice(i, 1);
      else group.selectedIds.push(id);
    },
    allPickedInGroup(group) {
      return (
        group.ids.length > 0 && group.selectedIds.length === group.ids.length
      );
    },
    pickAllInGroup(group) {
      if (this.allPickedInGroup(group)) {
        group.selectedIds = [];
      } else {
        group.selectedIds = [...group.ids];
      }
    },
    async moveSelectedTo(fromGroup, toIdx) {
      const to = this.groups[toIdx];
      if (!to || to === fromGroup) return;
      if (to.result && to.result.ok) return;
      const moving = [...fromGroup.selectedIds];
      if (!moving.length) return;
      fromGroup.ids = fromGroup.ids.filter((x) => !moving.includes(x));
      fromGroup.selectedIds = [];
      to.ids.push(...moving);
      await this.refreshGroupDrafts([fromGroup, to]);
    },
    formatDate(v) {
      if (!v) return "";
      const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      return String(v);
    },
    async submit() {
      const pending = this.pendingGroups;
      if (!pending.length) return;
      this.saving = true;
      try {
        const payload = {
          groups: pending.map((g) => ({
            wings_otpremnica_ids: g.ids,
            cosignee: g.cosignee,
            sender_id: g.form.sender_id,
            reference: g.form.reference || null,
            payment_by: g.form.payment_by || "sender",
            number_of_packages: Number(g.form.number_of_packages) || 1,
            is_pallet: !!g.form.is_pallet,
            cod_amount:
              g.form.cod_amount != null && g.form.cod_amount !== ""
                ? Number(g.form.cod_amount)
                : null,
            note: g.form.note || null,
          })),
        };

        const {
          data,
        } = await axios.post(
          "/super-admin/city-express/shipments/batch",
          payload,
          { validateStatus: (s) => s === 201 || s === 207 },
        );

        // Map results back onto pending groups by index.
        const results = data.data || [];
        results.forEach((r, i) => {
          const g = pending[i];
          if (!g) return;
          g.result = { ok: r.ok, error: r.error, shipment: r.shipment };
          // Collapse cards that succeeded; keep failed ones expanded so the
          // operator can see + fix the reported error inline.
          if (r.ok) g.expanded = false;
          else g.expanded = true;
        });

        const okCount = results.filter((r) => r.ok).length;
        const failCount = results.length - okCount;
        if (failCount === 0) {
          toast(`Uspešno kreirano ${okCount} pošiljki.`, variants.SUCCESS);
          this.$emit("done", { okCount, failCount });
        } else if (okCount === 0) {
          toast(`Sve pošiljke odbijene (${failCount}).`, variants.DANGER);
        } else {
          toast(
            `Uspešno: ${okCount}, neuspešno: ${failCount}.`,
            variants.WARNING,
          );
        }
      } catch (err) {
        const errs =
          err.response && err.response.data && err.response.data.errors;
        if (errs) {
          const first = Object.values(errs)[0];
          toast(
            Array.isArray(first) ? first[0] : String(first),
            variants.DANGER,
          );
        } else {
          toast(
            (err.response && err.response.data && err.response.data.message) ||
              err.message ||
              "Greška pri slanju.",
            variants.DANGER,
          );
        }
      } finally {
        this.saving = false;
      }
    },
    rowLabel(id) {
      const r = this.rowsById[id];
      if (!r) return id;
      const profaktura = r.brojProfakture ? ` — ${r.brojProfakture}` : "";
      return `${id}${profaktura} — ${r.kupac || ""}${
        r.objekat ? " / " + r.objekat : ""
      }`;
    },
    formatMoney(v) {
      if (v === null || v === undefined || v === "") return "";
      return Number(v).toLocaleString("sr-RS", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<template>
  <div class="bigz-batch-preview">
    <!-- Saving overlay: visible while we POST /shipments/batch. Blocks
         every interaction in the modal so the operator can't double-click
         submit or edit groups while the request is in flight. -->
    <div v-if="saving" class="bigz-saving-overlay">
      <div class="text-center">
        <b-spinner />
        <div class="mt-2">
          Slanje {{ pendingGroups.length }} pošiljki u City Express…
        </div>
        <div class="small text-muted mt-1">
          Može potrajati nekoliko sekundi po pošiljci.
        </div>
      </div>
    </div>
    <div v-if="loading" class="text-center py-4">
      <b-spinner small /> Učitavanje pregleda…
    </div>

    <template v-else>
      <!-- Global sender picker. Changing this propagates the choice to every
           pending group; per-group override is still available when a group
           is expanded. -->
      <div
        v-if="pendingGroups.length"
        class="d-flex align-items-center mb-2"
        style="gap: 0.5rem"
      >
        <label class="mb-0 small text-muted" style="white-space: nowrap">
          Pošiljalac za sve grupe:
        </label>
        <b-form-select
          size="sm"
          :value="globalSenderId"
          @change="applyGlobalSender"
          style="max-width: 360px"
        >
          <option :value="null">— odaberi pošiljaoca —</option>
          <option v-for="s in senders" :key="s.id" :value="s.id">
            {{ s.name }}{{ s.city ? ` (${s.city})` : "" }}
          </option>
        </b-form-select>
      </div>

      <!-- Group actions toolbar -->
      <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 0.5rem">
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="regrouping"
          @click="regroupByKupac()"
        >
          <b-spinner v-if="regrouping" small class="mr-1" />
          Auto-grupiši po kupcu
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="regrouping"
          @click="splitToOnePer()"
        >
          <b-spinner v-if="regrouping" small class="mr-1" />
          Razgrupiši
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="regrouping"
          @click="addEmptyGroup()"
        >
          + Prazna grupa
        </b-button>
        <span class="text-muted small ml-2">|</span>
        <b-button
          size="sm"
          variant="link"
          :disabled="regrouping"
          @click="expandAll()"
        >
          Otvori sve
        </b-button>
        <b-button
          size="sm"
          variant="link"
          :disabled="regrouping"
          @click="collapseAll()"
        >
          Zatvori sve
        </b-button>
      </div>

      <!-- Groups (collapsed by default; locked while regrouping or saving) -->
      <div :class="{ 'bigz-groups-locked': regrouping || saving }">
        <div
          v-for="(group, idx) in groups"
          :key="idx"
          class="card mb-2"
          :class="{
            'border-success': group.result && group.result.ok,
            'border-danger': group.result && !group.result.ok,
          }"
        >
          <!-- HEADER: always visible, click to expand -->
          <div
            class="card-header py-2 d-flex flex-wrap align-items-center bigz-group-header"
            @click="toggleExpand(group)"
          >
            <span class="mr-2 text-muted" style="width: 1rem">
              {{ group.expanded ? "▾" : "▸" }}
            </span>
            <strong class="mr-2">#{{ idx + 1 }}</strong>
            <span class="mr-3 text-truncate" style="max-width: 360px">
              {{ headerTitle(group) }}
            </span>
            <b-badge
              v-b-tooltip.hover
              :variant="addressChip(group).variant"
              :title="addressChip(group).tip || ''"
              class="mr-2"
              style="font-weight: normal"
            >
              {{ addressChip(group).text }}
            </b-badge>
            <b-badge
              v-if="
                !(group.result && group.result.ok) && groupInvalidReason(group)
              "
              variant="danger"
              class="mr-2"
              style="font-weight: normal"
              :title="groupInvalidReason(group)"
            >
              ⚠ {{ groupInvalidReason(group) }}
            </b-badge>
            <span class="small text-muted mr-2">
              {{ group.ids.length }}
              {{ group.ids.length === 1 ? "otpremnica" : "otpremnica" }} ·
              {{ groupTotalPackages(group) }} pak ·
              {{ formatMoney(groupTotalValue(group)) }} RSD
            </span>
            <span class="ml-auto" @click.stop>
              <b-badge
                v-if="group.result && group.result.ok"
                variant="success"
                class="mr-2"
              >
                ✓ kreirano (CE
                {{
                  group.result.shipment && group.result.shipment.shipment_id
                }})
              </b-badge>
              <b-badge
                v-else-if="group.result && !group.result.ok"
                variant="danger"
                class="mr-2"
              >
                ✗ greška
              </b-badge>
              <b-button
                v-if="!(group.result && group.result.ok)"
                size="sm"
                variant="outline-danger"
                @click.stop="removeGroup(idx)"
              >
                Ukloni
              </b-button>
            </span>
          </div>

          <!-- EXPANDED BODY -->
          <div v-if="group.expanded" class="card-body">
            <div
              v-if="group.result && !group.result.ok"
              class="alert alert-danger py-2"
            >
              {{ group.result.error || "Nepoznata greška." }}
            </div>

            <!-- Otpremnice as a compact table (D) with bulk-select (E) -->
            <div class="mb-3">
              <div class="d-flex align-items-center mb-1" style="gap: 0.5rem">
                <strong class="mr-2">Otpremnice</strong>
                <b-button
                  v-if="group.ids.length > 1"
                  size="sm"
                  variant="link"
                  class="p-0"
                  @click="pickAllInGroup(group)"
                >
                  {{ allPickedInGroup(group) ? "Poništi sve" : "Izaberi sve" }}
                </b-button>
                <span
                  v-if="
                    group.selectedIds.length &&
                      groups.length > 1 &&
                      !(group.result && group.result.ok)
                  "
                  class="ml-auto d-flex align-items-center"
                  style="gap: 0.25rem"
                >
                  <span class="small text-muted">
                    {{ group.selectedIds.length }} izabrano
                  </span>
                  <b-form-select
                    size="sm"
                    :value="''"
                    style="width: auto"
                    @change="(v) => moveSelectedTo(group, Number(v))"
                  >
                    <option value="" disabled>Premesti u…</option>
                    <option
                      v-for="(g, j) in groups"
                      :key="j"
                      :value="j"
                      :disabled="g === group || (g.result && g.result.ok)"
                    >
                      Pošiljka #{{ j + 1 }}
                    </option>
                  </b-form-select>
                </span>
              </div>
              <table class="table table-sm bigz-otp-table mb-0">
                <thead class="text-muted">
                  <tr>
                    <th style="width: 30px"></th>
                    <th>ID</th>
                    <th>Broj otpremnice</th>
                    <th>Profaktura</th>
                    <th>Datum</th>
                    <th class="text-right">Pak.</th>
                    <th class="text-right">Vrednost</th>
                    <th style="width: 30px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="id in group.ids" :key="id">
                    <td>
                      <b-form-checkbox
                        :checked="group.selectedIds.includes(id)"
                        @change="pickToggleId(group, id)"
                      />
                    </td>
                    <td>{{ id }}</td>
                    <td>{{ rowFor(id).broj || "—" }}</td>
                    <td>{{ rowFor(id).brojProfakture || "—" }}</td>
                    <td>{{ formatDate(rowFor(id).datum) }}</td>
                    <td class="text-right">
                      {{
                        rowFor(id).brojPaketa != null
                          ? rowFor(id).brojPaketa
                          : "—"
                      }}
                    </td>
                    <td class="text-right">
                      {{ formatMoney(rowFor(id).vrednost) }}
                    </td>
                    <td class="text-right" style="white-space: nowrap">
                      <b-button
                        v-if="
                          group.ids.length > 1 &&
                            !(group.result && group.result.ok)
                        "
                        size="sm"
                        variant="link"
                        class="p-0 mr-2"
                        @click="extractToOwnGroup(group, id)"
                        title="Izdvoji u zasebnu pošiljku"
                      >
                        ✂
                      </b-button>
                      <b-button
                        v-if="!(group.result && group.result.ok)"
                        size="sm"
                        variant="link"
                        class="text-danger p-0"
                        @click="removeFromGroup(group, id)"
                        title="Izbaci iz grupe"
                      >
                        ×
                      </b-button>
                    </td>
                  </tr>
                  <tr v-if="!group.ids.length">
                    <td colspan="7" class="text-muted text-center py-2">
                      Nema otpremnica u grupi.
                    </td>
                  </tr>
                </tbody>
              </table>
              <div
                v-if="!singleKupacInGroup(group)"
                class="text-danger small mt-1"
              >
                Sve otpremnice u grupi moraju biti za istog kupca.
              </div>
            </div>

            <fieldset :disabled="group.result && group.result.ok">
              <div class="row">
                <div class="col-md-6">
                  <h6 class="text-muted">Primalac</h6>
                  <div class="form-group">
                    <label>Naziv *</label>
                    <b-form-input
                      v-model="group.cosignee.name"
                      maxlength="35"
                    />
                  </div>
                  <div class="form-group">
                    <label>Adresa *</label>
                    <b-form-input
                      v-model="group.cosignee.street_and_number"
                      maxlength="70"
                    />
                  </div>
                  <div class="row">
                    <div class="col-4">
                      <div class="form-group">
                        <label>PB *</label>
                        <b-form-input
                          v-model="group.cosignee.zipcode"
                          maxlength="10"
                        />
                      </div>
                    </div>
                    <div class="col-8">
                      <div class="form-group">
                        <label>Grad *</label>
                        <b-form-input
                          v-model="group.cosignee.city"
                          maxlength="35"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="form-group">
                    <label>Država *</label>
                    <b-form-input
                      v-model="group.cosignee.country_code"
                      maxlength="2"
                    />
                  </div>
                  <div class="form-group">
                    <label>Kontakt osoba</label>
                    <b-form-input
                      v-model="group.cosignee.contact_name"
                      maxlength="255"
                    />
                  </div>
                  <div class="row">
                    <div class="col-6">
                      <div class="form-group">
                        <label>Telefon</label>
                        <b-form-input
                          v-model="group.cosignee.phone"
                          maxlength="30"
                        />
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="form-group">
                        <label>Email</label>
                        <b-form-input
                          v-model="group.cosignee.email"
                          type="email"
                          maxlength="70"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <h6 class="text-muted">Pošiljka</h6>
                  <div class="form-group">
                    <label>Pošiljalac</label>
                    <b-form-select v-model="group.form.sender_id">
                      <option :value="null">— bez podrazumevanog —</option>
                      <option v-for="s in senders" :key="s.id" :value="s.id">
                        {{ s.name }} ({{ s.city }})
                      </option>
                    </b-form-select>
                  </div>
                  <div class="form-group">
                    <label>Referenca</label>
                    <b-form-input
                      v-model="group.form.reference"
                      maxlength="105"
                      @input="markDirty(group, 'reference')"
                    />
                  </div>
                  <div class="form-group">
                    <label>Broj paketa *</label>
                    <b-form-input
                      v-model.number="group.form.number_of_packages"
                      type="number"
                      min="1"
                      max="999"
                      @input="markDirty(group, 'number_of_packages')"
                    />
                  </div>
                  <div class="form-group">
                    <b-form-checkbox v-model="group.form.is_pallet" switch>
                      Paleta
                    </b-form-checkbox>
                    <small class="text-muted d-block">
                      Pošiljka se šalje kao paleta (obračun po paleti).
                    </small>
                  </div>
                  <div class="form-group">
                    <b-form-checkbox
                      v-model="group.form.payment_by"
                      value="recipient"
                      unchecked-value="sender"
                      switch
                    >
                      Primalac plaća poštarinu
                    </b-form-checkbox>
                  </div>
                  <div class="form-group">
                    <label>Otkup (COD)</label>
                    <b-form-input
                      v-model.number="group.form.cod_amount"
                      type="number"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div class="form-group">
                    <label>Napomena</label>
                    <b-form-textarea
                      v-model="group.form.note"
                      rows="2"
                      max-rows="4"
                      @input="markDirty(group, 'note')"
                    />
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
        </div>
      </div>

      <!-- Sticky footer (C) with totals + submit -->
      <div class="bigz-batch-footer">
        <div class="d-flex flex-wrap align-items-center" style="gap: 1rem">
          <span class="small">
            <span class="text-success"
              >Spremno: <strong>{{ readyCount }}</strong></span
            >
            <span v-if="invalidCount" class="text-danger ml-2">
              Nevažeće: <strong>{{ invalidCount }}</strong>
            </span>
            <span v-if="successCount" class="text-success ml-2">
              Poslato: <strong>{{ successCount }}</strong>
            </span>
            <span v-if="failureCount" class="text-danger ml-2">
              Odbijeno: <strong>{{ failureCount }}</strong>
            </span>
          </span>
          <span class="small text-muted">
            Ukupno paketa: <strong>{{ totalPackagesAll }}</strong> · Ukupna
            vrednost: <strong>{{ formatMoney(totalValueAll) }} RSD</strong>
          </span>
          <span class="ml-auto">
            <b-button
              variant="secondary"
              @click="$emit('cancel')"
              :disabled="saving"
            >
              {{ successCount > 0 ? "Zatvori" : "Otkaži" }}
            </b-button>
            <b-button
              v-if="pendingGroups.length"
              type="button"
              variant="primary"
              class="ml-2"
              :disabled="!canSubmit"
              @click="submit()"
            >
              {{
                saving ? "Slanje…" : `Kreiraj ${pendingGroups.length} pošiljki`
              }}
            </b-button>
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.bigz-group-header {
  cursor: pointer;
  user-select: none;
}
.bigz-group-header:hover {
  background-color: #f8f9fa;
}
.bigz-otp-table th,
.bigz-otp-table td {
  padding: 0.25rem 0.5rem;
  vertical-align: middle;
  font-size: 0.875rem;
}
.bigz-batch-footer {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #dee2e6;
  padding: 0.75rem 0;
  margin-top: 1rem;
  z-index: 5;
}
.bigz-groups-locked {
  pointer-events: none;
  opacity: 0.55;
}
.bigz-batch-preview {
  position: relative;
}
.bigz-saving-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
</style>
